import React, {
  createContext,
  useContext,
  useState,
} from "react";
import {
  List,
  TextField as RaTextField,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
  useSidebarState,
  useTranslate,
} from "react-admin";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  CustomDatagrid,
  FilterField,
  FILTER_HEIGHT,
  ListActionsBar,
  NamedDeleteButton,
} from "@/components/common";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { useCanCreate, useCanEdit, useCanDelete } from "@/utils/permissions";
import LicenseFormDialog, { type License } from "./LicenseFormDialog";

const i18nBase = "resources.licenses";

interface RowActionContextValue {
  openEdit: (record: License) => void;
}

const RowActionContext = createContext<RowActionContextValue | null>(null);
const useRowActions = (): RowActionContextValue => {
  const ctx = useContext(RowActionContext);
  if (!ctx) {
    throw new Error("Row action used outside LicensesList");
  }
  return ctx;
};

const EditRowButton: React.FC = () => {
  const record = useRecordContext<License>();
  const translate = useTranslate();
  const { openEdit } = useRowActions();
  if (!record) return null;
  return (
    <Button
      size="small"
      variant="text"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={(e) => {
        e.stopPropagation();
        openEdit(record);
      }}
      sx={{ textTransform: "none", fontWeight: 500, minWidth: 0 }}
    >
      {translate("ra.action.edit")}
    </Button>
  );
};

interface ListActionsProps {
  onAdd: () => void;
}

const LicensesListActions: React.FC<ListActionsProps> = ({ onAdd }) => {
  const translate = useTranslate();
  const canCreate = useCanCreate();
  return (
    <ListActionsBar
      filters={
        <>
          <FilterField
            source="licenseName"
            label={translate(`${i18nBase}.filters.licenseName`)}
          />
        </>
      }
      addButton={
        canCreate ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onAdd}
            startIcon={<AddIcon />}
            aria-label={translate(`${i18nBase}.actions.addLicense`)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
              height: FILTER_HEIGHT,
              minWidth: { xs: FILTER_HEIGHT, sm: "auto" },
              borderRadius: 1.5,
              px: { xs: 0, sm: 2.5 },
              boxShadow: "none",
              "& .MuiButton-startIcon": {
                mr: { xs: 0, sm: 1 },
                ml: { xs: 0, sm: -0.5 },
              },
              "&:hover": { boxShadow: "0 2px 6px rgba(11, 165, 183, 0.25)" },
            }}
          >
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              {translate(`${i18nBase}.actions.addLicense`)}
            </Box>
          </Button>
        ) : null
      }
    />
  );
};

export const LicensesList: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";
  const dataProvider = useDataProvider();
  const [editing, setEditing] = useState<License | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const canEdit = useCanEdit();
  const canDelete = useCanDelete();

  const openEdit = async (record: License): Promise<void> => {
    try {
      const { data } = await dataProvider.getOne<License>("licenses", {
        id: record.id,
      });
      setEditing(data);
      setFormOpen(true);
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          `${i18nBase}.notifications.loadFailed`,
          translate,
        ),
        { type: "error" },
      );
    }
  };

  const openAdd = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const handlers: RowActionContextValue = { openEdit };

  return (
    <RowActionContext.Provider value={handlers}>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
            mt: 0,
            ml: 2,
          }}
        >
          {translate(`${i18nBase}.name`, { smart_count: 2 })}
        </Typography>
        <List
          actions={<LicensesListActions onAdd={openAdd} />}
          sort={{ field: "0", order: "ASC" }}
          storeKey={false}
          disableSyncWithLocation
          perPage={10}
          exporter={false}
          sx={{
            maxWidth,
            overflow: "visible",
            "& .RaList-main, & .RaList-content": {
              overflow: "visible",
              width: "100%",
              maxWidth: "100%",
            },
            "& .RaListToolbar-root, & .RaList-actions": {
              display: "flex",
              width: "100%",
              mb: 2,
              px: 0,
              py: { xs: 0.5, sm: 1 },
              minHeight: "auto",
            },
          }}
        >
          <CustomDatagrid primarySource="name" bulkActionButtons={false} disableMobileView>
            <RaTextField source="name" />
            {canEdit && <EditRowButton />}
            {canDelete && <NamedDeleteButton displayField="name" />}
          </CustomDatagrid>
        </List>

        <LicenseFormDialog
          record={editing}
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSuccess={() => {
            setFormOpen(false);
            refresh();
          }}
        />
      </Box>
    </RowActionContext.Provider>
  );
};

export default LicensesList;
